import { FC, ReactNode, createContext, useContext, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { GET_USER_TIMEZONE } from "@queries/index";
import { TZDateMini } from "@date-fns/tz";
import { User } from "@archetypes/auth";
import { useToastContext } from "./toast";
import { format } from "date-fns";
import { getUTCOffsetString } from "@utils/date.util";
import { isNullOrUndefined } from "@utils/data-type.util";
import { DateFormat, DateRange } from "@archetypes/form";
import { NO_DATA } from "@constants/form";

type ITimezoneProvider = {
  timezone?: string;
  formatDate: (date?: string | DateRange, dateFormat?: string) => string;
  loading: boolean;
  error?: unknown;
};

const TimezoneContext = createContext<ITimezoneProvider | null>(null);

export const TimezoneProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { showToast } = useToastContext();
  const { data, loading, error } = useQuery<{
    getProfileDetails: User;
  }>(GET_USER_TIMEZONE, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!error) {
      return;
    }

    showToast({
      title: "Error",
      content: `Error fetching timezone data`,
      variant: "danger",
    });
  }, [error, showToast]);

  const value = useMemo<ITimezoneProvider>(() => {
    return {
      timezone: data?.getProfileDetails?.timeZone,
      formatDate: (date?: string | DateRange, dateFormat = DateFormat.dayMonthYear) => {
        try {
          if (!date) {
            return NO_DATA;
          }
          const offsetString = getUTCOffsetString(data?.getProfileDetails?.timeZone);
          if (typeof date === "string") {
            const zonedDate = new TZDateMini(date, offsetString);
            return format(zonedDate, dateFormat);
          } else if (
            typeof date === "object" &&
            !isNullOrUndefined((date as DateRange).start) &&
            !isNullOrUndefined((date as DateRange).end)
          ) {
            const zonedStartDate = new TZDateMini(date.start as string, offsetString);
            const zonedEndDate = new TZDateMini(date.end as string, offsetString);
            return `${format(zonedStartDate, dateFormat)} - ${format(zonedEndDate, dateFormat)}`;
          }
          return "";
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Error formatting date:", err);
          return NO_DATA;
        }
      },
      loading,
      error,
    };
  }, [data, loading, error]);

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};

export function useTimezone() {
  const ctx = useContext(TimezoneContext);
  if (!ctx) throw new Error("useTimezoneProvider must be used inside <TimezoneProvider>");
  return ctx;
}
